import {
  FC,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useQuery } from '@apollo/client';
import {
  BackwardIcon,
  Button,
  DatePicker,
  IDropdownItem,
  NextArrowIcon,
  Preloader,
  Select,
  Tooltip,
} from '@smartshell/ui-kit';
import cn from 'classnames';
import dayjs from 'dayjs';

import { AuthContext } from 'auth';
import { FeatureHOC, useIsFeatureAvailable } from 'features';
import { useWindowResize } from 'hooks';
import { LocalizationContext } from 'localization';
import {
  BookingModal,
  ClientModal,
  DepositModal,
  NotPermissionModal,
  ResetPasswordModal,
} from 'modals';
import { SettingsContext } from 'settings';
import { EBookingStatus, IUser } from 'types';

import { Booking } from './Booking';
import s from './Bookings.module.scss';
import { ALL_GROUPS_VALUE, hours } from './constants';
import { GET_BOOKINGS_MOCKED_RESPONSE, HOSTS_MOCKED_RESPONSE } from './mocks';
import { BOOKINGS, HOST_GROUPS } from './schema';
import {
  IBooking,
  IBookingsRequest,
  IBookingsResponse,
  IHostBooking,
  IHostGroupsResponse,
} from './types';
import { useDragScroll } from './useDragScroll';
import { getHostSenses, getNormalizeBooking } from './utils';

export const Bookings: FC = () => {
  const { t, lang } = useContext(LocalizationContext);
  const {
    data: { permissions },
  } = useContext(AuthContext);
  const {
    state: {
      settings: { timezone, showSeans, bookingStrictModeHeadroom },
    },
  } = useContext(SettingsContext);

  const [date, setDate] = useState<string | null>(
    dayjs().hour(0).minute(0).second(0).format()
  );
  const [time, setTime] = useState<string | null>();
  const [selectedHostsIds, setSelectedHostsIds] = useState<Array<number>>([]);
  const [selectedGroup, setSelectedGroup] = useState(ALL_GROUPS_VALUE);
  const hoursRef = useRef<HTMLDivElement>(null);
  const [hoursWidth, setHoursWidth] = useState<number>(0);
  const pageRef = useRef<HTMLDivElement>(null);
  const [selectedClient, setSelectedClient] = useState<string | null>(null);
  const [selectedBooking, setSelectedBooking] = useState<number | null>(null);
  const [isBookingOpen, setIsBookingOpen] = useState<boolean>(false);
  const [isDepositOpen, setIsDepositOpen] = useState(false);
  const [defaultClient, setIsDefaultClient] = useState<string | undefined>('');
  const [downCoords, setDownCoords] = useState<number | null>(null);
  const [isResetPasswordModal, setIsResetPasswordModal] = useState<
    boolean | undefined
  >(false);
  const [currentClient, setCurrentClient] = useState<IUser | undefined>(
    undefined
  );
  const [isHardBooking, setIsHardBooking] = useState<boolean>(false);
  const [leftSwipeProgress, setLeftSwipeProgress] = useState(0);
  const [rightSwipeProgress, setRightSwipeProgress] = useState(0);

  const isBookingAvailable = useIsFeatureAvailable('booking');
  const isSwitchingDay = useRef(false);
  const size = useWindowResize();

  const dragScrollProps = useDragScroll({
    onSwipeLeftEdge: () => {
      if (isSwitchingDay.current) return;
      isSwitchingDay.current = true;
      const newDate = dayjs(date).subtract(1, 'days').format('YYYY-MM-DD');
      setDate(newDate);
      if (dragScrollProps.ref.current) {
        dragScrollProps.ref.current.scrollLeft = 0;
      }
      setTimeout(() => {
        isSwitchingDay.current = false;
      }, 500);
    },
    onSwipeRightEdge: () => {
      if (isSwitchingDay.current) return;
      isSwitchingDay.current = true;
      const newDate = dayjs(date).add(1, 'days').format('YYYY-MM-DD');
      setDate(newDate);
      if (dragScrollProps.ref.current) {
        dragScrollProps.ref.current.scrollLeft = 0;
      }
      setTimeout(() => {
        isSwitchingDay.current = false;
      }, 500);
    },
    onEdgeSwipeProgress: (side, progress) => {
      if (side === 'left' && size.width > 960) setLeftSwipeProgress(progress);
      if (side === 'right' && size.width > 960) setRightSwipeProgress(progress);
    },
    minSwipeDistance: 400,
  });

  const mapControlsRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    requestAnimationFrame(() => {
      setHoursWidth(hoursRef.current?.scrollWidth ?? 0);
    });
  }, [hoursRef.current]);

  const currentHours = useMemo(
    () => (dayjs().diff(dayjs(date), 'hours') - 2) * 60 + 45,
    [date]
  );

  useEffect(() => {
    if (dragScrollProps.ref.current) {
      dragScrollProps.ref.current.scrollLeft = currentHours + 800;
    }
  }, [dragScrollProps.ref.current, currentHours]);

  useEffect(() => {
    const syncScrollPositions = (): void => {
      const rightElement = dragScrollProps.ref.current;
      const mapControlsElement = mapControlsRef.current;

      if (rightElement && mapControlsElement) {
        mapControlsElement.scrollLeft = rightElement.scrollLeft;
      }
    };

    syncScrollPositions();

    let observer: MutationObserver | null = null;

    const initSyncScroll = (): (() => void) | void => {
      const rightElement = dragScrollProps.ref.current;
      const mapControlsElement = mapControlsRef.current;

      if (!rightElement || !mapControlsElement) return;

      let isSyncingRight = false;
      let isSyncingMapControls = false;

      const handleRightScroll = (): void => {
        if (isSyncingRight) {
          isSyncingRight = false;
          return;
        }
        isSyncingMapControls = true;
        mapControlsElement.scrollLeft = rightElement.scrollLeft;
      };

      const handleMapControlsScroll = (): void => {
        if (isSyncingMapControls) {
          isSyncingMapControls = false;
          return;
        }
        isSyncingRight = true;
        rightElement.scrollLeft = mapControlsElement.scrollLeft;
      };

      rightElement.addEventListener('scroll', handleRightScroll);
      mapControlsElement.addEventListener('scroll', handleMapControlsScroll);

      return () => {
        rightElement.removeEventListener('scroll', handleRightScroll);
        mapControlsElement.removeEventListener(
          'scroll',
          handleMapControlsScroll
        );
      };
    };

    const startObserver = (): void => {
      observer = new MutationObserver(() => {
        const rightElement = dragScrollProps.ref.current;
        const mapControlsElement = mapControlsRef.current;

        if (rightElement && mapControlsElement) {
          if (observer) observer.disconnect();
          initSyncScroll();
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
    };

    startObserver();

    return () => {
      if (observer) observer.disconnect();
    };
  }, [dragScrollProps, mapControlsRef]);

  useEffect(() => {
    const rightElement = dragScrollProps.ref.current;
    const mapControlsElement = mapControlsRef.current;

    if (rightElement && mapControlsElement) {
      mapControlsElement.scrollLeft = rightElement.scrollLeft;
    }
  }, [dragScrollProps.ref, mapControlsRef, currentHours]);

  const { data: hostGroupsData } = useQuery<IHostGroupsResponse>(HOST_GROUPS, {
    fetchPolicy: 'no-cache',
    skip: !isBookingAvailable || !permissions?.has('booking_manage'),
  });

  const hostGroupsResponse = isBookingAvailable
    ? hostGroupsData
    : HOSTS_MOCKED_RESPONSE;

  const { groupTitleById, sortedHosts, hostGroupsOptions } = useMemo(() => {
    const groupTitleById =
      hostGroupsResponse?.hostGroupsOverview.reduce<Record<number, string>>(
        (acc, group) => {
          acc[group.id] = group.title;
          return acc;
        },
        {}
      ) ?? {};

    const sortedHosts = [
      ...(hostGroupsResponse?.hostsOverview ?? []).sort(
        (
          { position: p1 = 0, group_id: g1 },
          { position: p2 = 0, group_id: g2 }
        ) => {
          if (g1 === g2) {
            return p1 - p2;
          }

          return g1 - g2;
        }
      ),
    ];

    const hostGroupsOptions: Array<IDropdownItem<number>> =
      hostGroupsResponse?.hostGroupsOverview.map((group) => ({
        title: group.title,
        value: group.id,
      })) ?? [];

    hostGroupsOptions.unshift({
      value: ALL_GROUPS_VALUE,
      title: t('hosts.allGroups'),
    });

    return { groupTitleById, sortedHosts, hostGroupsOptions };
  }, [hostGroupsResponse, t]);

  const { data: bookingsData, loading: isBookingsLoading } = useQuery<
    IBookingsResponse,
    IBookingsRequest
  >(BOOKINGS, {
    fetchPolicy: 'no-cache',
    skip: !isBookingAvailable || !permissions?.has('booking_manage'),
    variables: {
      from: dayjs(date).format('YYYY-MM-DD 00:00:00'),
      to: dayjs(date).add(1, 'days').format('YYYY-MM-DD 12:00:00'),
    },
  });

  const bookingsResponse = isBookingAvailable
    ? bookingsData
    : GET_BOOKINGS_MOCKED_RESPONSE();

  const normalizedBookings = useMemo(() => {
    const normalizedBookings: Array<IHostBooking> = getNormalizeBooking(
      bookingsResponse,
      sortedHosts
    );

    if (showSeans) {
      const senses = getHostSenses(
        hostGroupsData?.hostsOverview,
        date,
        timezone
      );

      normalizedBookings.push(...senses);
    }

    return normalizedBookings;
  }, [
    bookingsResponse?.getBookings.data,
    hostGroupsData?.hostsOverview,
    sortedHosts,
    timezone,
    showSeans,
  ]);

  const { filteredHosts, filteredBookings } = useMemo(() => {
    if (selectedGroup === ALL_GROUPS_VALUE) {
      return {
        filteredHosts: sortedHosts,
        filteredBookings: normalizedBookings,
      };
    }

    const filteredHosts = sortedHosts.filter(
      (host) => host.group_id === selectedGroup
    );

    return {
      filteredHosts,
      filteredBookings: normalizedBookings.filter((booking) =>
        filteredHosts.some((host) => host.id === booking.host.id)
      ),
    };
  }, [sortedHosts, selectedGroup, normalizedBookings]);

  const [notPermissionModalOpen, setNotPermissionModalOpen] =
    useState<boolean>(false);

  const totalBlocks = filteredHosts.length * 72;

  const getAdjustedTime = (
    time: string,
    bookings: Array<IBooking> | undefined,
    clientSessions:
      | Array<{ started_at: string; finished_at: string }>
      | undefined,
    bookingStrict: number,
    selectedDate: string | null
  ): string => {
    const currentDate = dayjs(selectedDate, 'YYYY-MM-DD');
    const timeMoment = currentDate
      .hour(parseInt(time.split(':')[0]))
      .minute(parseInt(time.split(':')[1]));

    const combinedSchedules = [
      ...(bookings || []).map((booking) => ({
        from: booking.from,
        to: booking.to,
      })),
      ...(clientSessions || []).map((session) => ({
        from: session.started_at,
        to: session.finished_at,
      })),
    ];

    const overlappingSchedule = combinedSchedules.find((schedule) => {
      const from = dayjs(schedule.from);
      const to = dayjs(schedule.to);
      return timeMoment.isBetween(from, to, null, '[]');
    });

    if (overlappingSchedule) {
      const adjustedTime = dayjs(overlappingSchedule.to).add(
        bookingStrict,
        'minutes'
      );

      if (adjustedTime.isAfter(currentDate.endOf('day'))) {
        const nextDay = currentDate.add(1, 'day');
        setDate(nextDay.format('YYYY-MM-DD'));
        return adjustedTime.subtract(1, 'day').startOf('day').format('HH:mm');
      }

      return adjustedTime.format('HH:mm');
    }

    const closestSchedule = combinedSchedules.reduce((closest, schedule) => {
      const to = dayjs(schedule.to);
      if (!closest || to.isAfter(dayjs(closest.to))) {
        return schedule;
      }
      return closest;
    }, null as { from: string; to: string } | null);

    if (closestSchedule) {
      const toTime = dayjs(closestSchedule.to);

      if (toTime.isSame(currentDate.endOf('day'))) {
        const adjustedTime = toTime.add(bookingStrict, 'minutes');
        const nextDay = currentDate.add(1, 'day');
        setDate(nextDay.format('YYYY-MM-DD'));
        return adjustedTime.startOf('day').format('HH:mm');
      }

      const diffInMinutes = timeMoment.diff(toTime, 'minutes');

      if (diffInMinutes > 0 && diffInMinutes < bookingStrict) {
        const adjustedTime = toTime.add(bookingStrict, 'minutes');

        if (adjustedTime.isAfter(currentDate.endOf('day'))) {
          const nextDay = currentDate.add(1, 'day');
          setDate(nextDay.format('YYYY-MM-DD'));
          return adjustedTime.startOf('day').format('HH:mm');
        }

        return adjustedTime.format('HH:mm');
      }
    }

    return timeMoment.format('HH:mm');
  };

  const handleMouseDown = (e: React.MouseEvent): void => {
    setDownCoords(e.clientX);
  };

  const handleMouseUp = (event: React.MouseEvent, index: number): void => {
    if (event.button === 2) return;
    if (event.clientX === downCoords) {
      const hostIndex = Math.floor(index / 72);
      const host = filteredHosts[hostIndex].id;
      const time = hours[index % (hours.length - 1)];
      const hostBookings = bookingsData?.getBookings.data.filter(
        (booking) =>
          booking.hostIds.includes(host) &&
          booking.status !== 'CANCELED' &&
          booking.status !== 'REDEEMED'
      );
      const hostSessions =
        hostGroupsData?.hostsOverview.find((h) => h.id === host)
          ?.client_sessions || [];

      let newDate = date;
      if (index % (hours.length - 1) >= 48) {
        const baseDate = dayjs(date);
        newDate = baseDate.add(1, 'day').format('YYYY-MM-DD');
        setDate(newDate);
      }

      const adjustedTime = getAdjustedTime(
        time,
        hostBookings,
        hostSessions,
        bookingStrictModeHeadroom,
        newDate
      );

      if (time !== adjustedTime) {
        setIsHardBooking(true);
      } else setIsHardBooking(false);

      setTime(adjustedTime);
      setSelectedHostsIds([host]);
      setIsBookingOpen(true);
    }
  };

  return (
    <FeatureHOC alias="booking" strategy="fade">
      {isDepositOpen && (
        <DepositModal
          defaultClientUUID={defaultClient}
          onClose={() => {
            setIsDepositOpen(false);
            setIsBookingOpen(false);
            setSelectedClient(null);
            setSelectedBooking(null);
          }}
        />
      )}
      <div className={s.root} ref={pageRef}>
        <div
          className={cn(s.arrow_wrapper, s.left_arrow, {
            [s.active]: leftSwipeProgress === 1,
          })}
          style={{
            transform: `translateY(${
              size.width > 1864 ? '50%' : '70%'
            }) translateX(${-150 + leftSwipeProgress * 210}%)`,
          }}
        >
          <div
            className={s.arrow_fill}
            style={{
              transform: `translateY(-50%) scale(${
                0.6 + (2 * leftSwipeProgress) / 4
              })`,
            }}
          />

          <span className={s.day}>
            <BackwardIcon />
            {dayjs(date).add(-1, 'days').format('DD MMMM')}
          </span>
        </div>

        <div
          className={cn(s.arrow_wrapper, s.right_arrow, {
            [s.active]: rightSwipeProgress === 1,
          })}
          style={{
            transform: `translateY(${
              size.width > 1864 ? '50%' : '70%'
            }) translateX(${120 - rightSwipeProgress * 200}%)`,
          }}
        >
          <div
            className={s.arrow_fill}
            style={{
              transform: `translateY(-50%) scale(${
                0.6 + (2 * rightSwipeProgress) / 4
              })`,
            }}
          />
          <span className={s.day}>
            {dayjs(date).add(1, 'days').format('DD MMMM')}
            <NextArrowIcon />
          </span>
        </div>
        {notPermissionModalOpen && (
          <NotPermissionModal
            onClose={() => setNotPermissionModalOpen(false)}
          />
        )}
        {selectedClient && !isDepositOpen && !isResetPasswordModal && (
          <ClientModal
            clientUUID={selectedClient}
            onClose={() => setSelectedClient(null)}
            setIsDepositOpen={setIsDepositOpen}
            setIsDefaultClient={setIsDefaultClient}
            setIsResetPasswordModal={setIsResetPasswordModal}
            setCurrentClient={setCurrentClient}
          />
        )}
        {isResetPasswordModal && (
          <ResetPasswordModal
            currentClient={currentClient}
            onClose={() => setIsResetPasswordModal(false)}
            clientUUID={selectedClient}
          />
        )}
        {(selectedBooking || isBookingOpen) && !isDepositOpen && (
          <BookingModal
            bookingId={selectedBooking ?? undefined}
            refetchQueries={[BOOKINGS]}
            onClose={() => {
              setSelectedBooking(null);
              setIsBookingOpen(false);
            }}
            setIsDefaultClient={setIsDefaultClient}
            setIsDepositOpen={setIsDepositOpen}
            selectedDate={date}
            selectedTime={time ? time : dayjs().format('HH:mm')}
            selectedHostsIds={selectedHostsIds}
            isHardBooking={isHardBooking}
          />
        )}
        <div className={s.controls}>
          <DatePicker
            date={date}
            setDate={setDate}
            title={t('booking.add.date')}
            withTime={false}
            lang={lang}
          />
          <Select
            name="host-group"
            title={t('bookings.view')}
            options={hostGroupsOptions}
            value={selectedGroup}
            errorWrapperClassName={s.groupSelect}
            onChange={(value) => setSelectedGroup(value ?? ALL_GROUPS_VALUE)}
            disableClear
          />
          <Button
            iconName="CirclePlusIcon"
            className={s.add}
            onClick={() => {
              setIsHardBooking(false);
              setTime(null);
              setSelectedHostsIds([]);
              setIsBookingOpen(true);
            }}
          >
            {t('bookings.book')}
          </Button>
        </div>
        <div className={s.grid}>
          <div className={s.left}>
            <span className={s.title}>
              {t('bookings.hosts')}
              <Preloader
                loading={isBookingsLoading}
                small
                className={s.preloader}
              />
            </span>
            {filteredHosts.length ? (
              <div className={s.hosts}>
                {filteredHosts.map((host) => (
                  <div key={`hosts-${host.id}`} className={s.host}>
                    {host.alias}
                    <span className={s.group}>
                      {groupTitleById[host.group_id]}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className={s.placeholder}>
                {t('bookings.hosts.placeholder')}
              </div>
            )}
          </div>
          <div className={s.right} {...dragScrollProps}>
            {dayjs().isBefore(dayjs(date).add(36, 'hours')) &&
              dayjs().isAfter(dayjs(date)) && (
                <div
                  className={s.currentTime}
                  style={{
                    left: dayjs().diff(dayjs(date), 'minutes') * 2 + 45,
                  }}
                />
              )}
            {filteredBookings?.map((booking, index) => {
              if (booking.status === EBookingStatus.REDEEMED_AUTO) return;
              if (booking.status === EBookingStatus.FINISHED) return;
              const top =
                66 +
                filteredHosts.findIndex((host) => host.id === booking.host.id) *
                  44;

              return (
                <Booking
                  key={`booking-${booking.id}-${booking.host}-${index}`}
                  openClient={(clientUUID) => {
                    if (!permissions?.has('client_info')) {
                      setNotPermissionModalOpen(true);
                      return;
                    }

                    setSelectedClient(clientUUID);
                  }}
                  openBooking={setSelectedBooking}
                  booking={booking}
                  date={dayjs(date).format('DD.MM.YYYY')}
                  containerWidth={hoursWidth}
                  top={top}
                  pageRef={pageRef}
                  scrollContainerRef={dragScrollProps.ref}
                />
              );
            })}
            <div className={s.zone}>
              {Array.from({ length: totalBlocks }).map((_, index) => {
                const endTime = hours[index % (hours.length - 1)];

                let slotDate = dayjs(date);
                if (index % (hours.length - 1) >= 48) {
                  slotDate = slotDate.add(1, 'day');
                }

                const [hoursStr, minutesStr] = endTime.split(':');
                const slotTime = slotDate
                  .set('hour', parseInt(hoursStr, 10))
                  .set('minute', parseInt(minutesStr, 10))
                  .set('second', 0)
                  .set('millisecond', 0);

                const isPast = slotTime.isBefore(dayjs());

                return !isPast && !isDepositOpen ? (
                  <Tooltip
                    key={index}
                    content={
                      <div className={s.tooltip_content}>
                        <div className={s.left_block}>
                          <span>{t('host')}</span>
                          <span>{t('time')}</span>
                        </div>
                        <div className={s.right_block}>
                          <span>
                            {filteredHosts[Math.floor(index / 72)].alias}
                          </span>
                          <span>
                            {hours[index % (hours.length - 1)]}-
                            {hours[(index % (hours.length - 1)) + 1]}
                          </span>
                        </div>
                      </div>
                    }
                    contentClassName={s.tooltip}
                    className={s.tooltip_frame}
                    duration={400}
                  >
                    <div
                      onMouseDown={handleMouseDown}
                      onMouseUp={(event) => handleMouseUp(event, index)}
                      key={index}
                      className={cn(s.zone_button, isPast && s.empty)}
                    ></div>
                  </Tooltip>
                ) : (
                  <div
                    onMouseDown={handleMouseDown}
                    onMouseUp={(event) => {
                      if (event.button === 2) return;
                      if (event.clientX === downCoords) {
                        const hostIndex = Math.floor(index / 72);
                        const host = filteredHosts[hostIndex].id;
                        const time = hours[index % (hours.length - 1)];
                        const hostBookings =
                          bookingsData?.getBookings.data.filter(
                            (booking) =>
                              booking.hostIds.includes(host) &&
                              booking.status !== 'CANCELED' &&
                              booking.status !== 'REDEEMED'
                          );
                        const hostSessions =
                          hostGroupsData?.hostsOverview.find(
                            (h) => h.id === host
                          )?.client_sessions || [];

                        let newDate = date;
                        if (index % (hours.length - 1) >= 48) {
                          const baseDate = dayjs(date);
                          newDate = baseDate.add(1, 'day').format('YYYY-MM-DD');
                          setDate(newDate);
                        }

                        const adjustedTime = getAdjustedTime(
                          time,
                          hostBookings,
                          hostSessions,
                          bookingStrictModeHeadroom,
                          newDate
                        );

                        if (time !== adjustedTime) {
                          setIsHardBooking(true);
                        } else setIsHardBooking(false);

                        setTime(adjustedTime);
                        setSelectedHostsIds([host]);
                        setIsBookingOpen(true);
                      }
                    }}
                    key={index}
                    className={cn(s.zone_button, isPast && s.empty)}
                  ></div>
                );
              })}
            </div>
          </div>
          <div className={s.hours_wrapper}>
            <div className={s.mapControls} ref={mapControlsRef}>
              <Button
                variant="ghost"
                disabled={isBookingsLoading}
                onClick={() =>
                  setDate(dayjs(date).subtract(1, 'days').format('YYYY-MM-DD'))
                }
              >
                <BackwardIcon size="s" />
              </Button>
              <div className={s.hours} ref={hoursRef}>
                {hours.map((hour, index) => (
                  <span
                    key={`hours-${index}`}
                    className={cn(s.hour, index % 2 !== 0 && s.hour_small)}
                  >
                    {hour}
                    <div className={s.separators}>
                      <div className={s.big} />
                      {index < hours.length - 1 && (
                        <>
                          <div className={s.small} />
                          <div className={s.medium} />
                          <div className={s.small} />
                        </>
                      )}
                    </div>
                  </span>
                ))}
              </div>
              <Button
                variant="ghost"
                className={s.next}
                disabled={isBookingsLoading}
                onClick={() =>
                  setDate(dayjs(date).add(1, 'days').format('YYYY-MM-DD'))
                }
              >
                <BackwardIcon size="s" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </FeatureHOC>
  );
};
